import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ProfilePicture from '@/components/ProfilePicture';
import { Send, MessageSquare, Flag, X, Check } from 'lucide-react';
import ReportConversationDialog from '@/components/ReportConversationDialog';

const timeLabel = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
};

export const DirectMessages = ({ currentUserId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(searchParams.get('conversation') || null);
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [selecting, setSelecting] = useState(false);      // choosing which messages to report
  const [selectedIds, setSelectedIds] = useState([]);
  const [reportOpen, setReportOpen] = useState(false);
  const bottomRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/messages/dm/conversations`);
      setConversations(r.data.conversations || []);
    } catch (e) { /* silent */ }
    finally { setLoadingList(false); }
  }, []);

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const r = await axios.get(`${API}/messages/dm/${convId}/messages`);
      setMessages(r.data.messages || []);
      setOther(r.data.other || null);
    } catch (e) { toast.error('Could not load conversation'); }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // poll conversation list + active thread every 5s for near-real-time
  useEffect(() => {
    if (selecting || reportOpen) return;   // don't shuffle messages mid-selection
    const t = setInterval(() => {
      loadConversations();
      if (activeId) loadMessages(activeId);
    }, 5000);
    return () => clearInterval(t);
  }, [activeId, loadConversations, loadMessages, selecting, reportOpen]);

  useEffect(() => {
    if (activeId) loadMessages(activeId);
  }, [activeId, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openConversation = (id) => {
    setActiveId(id);
    setSelecting(false);
    setSelectedIds([]);
    setSearchParams({ tab: 'direct', conversation: id });
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const cancelSelecting = () => { setSelecting(false); setSelectedIds([]); };

  const afterReport = (result) => {
    cancelSelecting();
    if (result?.disconnected || result?.blocked) {
      setActiveId(null);
      setMessages([]);
      setOther(null);
      setSearchParams({ tab: 'direct' });
    }
    loadConversations();
  };

  const send = async () => {
    const content = draft.trim();
    if (!content || !activeId) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/dm/${activeId}/send`, { content });
      setDraft('');
      await loadMessages(activeId);
      loadConversations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]" data-testid="direct-messages">
      {/* Conversation list */}
      <div className="border rounded-lg overflow-y-auto bg-white">
        <div className="p-3 border-b font-medium text-gray-700">Direct Messages</div>
        {loadingList ? (
          <p className="p-4 text-sm text-gray-400">Loading…</p>
        ) : conversations.length === 0 ? (
          <p className="p-4 text-sm text-gray-400">No conversations yet. Connect with a member and tap Message to start one.</p>
        ) : (
          conversations.map((c) => (
            <button
              key={c.id}
              onClick={() => openConversation(c.id)}
              data-testid={`conversation-${c.other.id}`}
              className={`w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 border-b ${activeId === c.id ? 'bg-cyan-50' : ''}`}
            >
              <ProfilePicture src={c.other.profile_picture} alt={c.other.full_name} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{c.other.full_name}</p>
                <p className="text-xs text-gray-500 truncate">{c.last_message || 'No messages yet'}</p>
              </div>
              {c.unread > 0 && (
                <span className="bg-cyan-500 text-white text-[11px] rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">{c.unread}</span>
              )}
            </button>
          ))
        )}
      </div>

      {/* Thread */}
      <div className="md:col-span-2 border rounded-lg flex flex-col bg-white">
        {!activeId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <MessageSquare className="w-10 h-10 mb-2" />
            <p className="text-sm">Select a conversation</p>
          </div>
        ) : (
          <>
            <div className="p-3 border-b flex items-center gap-2">
              {other && <ProfilePicture src={other.profile_picture} alt={other.full_name} size="sm" />}
              <p className="font-medium">{other?.full_name}</p>
              {!selecting && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelecting(true)}
                  className="ml-auto text-gray-400 hover:text-orange-600"
                  title="Report this conversation"
                  data-testid="dm-report-btn"
                >
                  <Flag className="w-4 h-4" />
                </Button>
              )}
            </div>

            {selecting && (
              <div
                className="px-3 py-2 bg-orange-50 border-b border-orange-200 flex items-center gap-2 flex-wrap"
                data-testid="dm-report-selection-bar"
              >
                <p className="text-xs text-orange-900 flex-1 min-w-[200px]">
                  Tap the messages you want the admins to see
                  {selectedIds.length > 0 && ` — ${selectedIds.length} selected`}
                </p>
                <Button
                  size="sm"
                  onClick={() => setReportOpen(true)}
                  disabled={selectedIds.length === 0}
                  className="bg-orange-600 hover:bg-orange-700 text-white h-8"
                  data-testid="dm-report-continue"
                >
                  <Check className="w-3.5 h-3.5 mr-1" /> Continue
                </Button>
                <Button
                  size="sm" variant="ghost"
                  onClick={cancelSelecting}
                  className="h-8 text-orange-900"
                  data-testid="dm-report-cancel-selection"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="dm-thread">
              {messages.map((m) => {
                const mine = m.sender_id === currentUserId;
                const picked = selectedIds.includes(m.id);
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div
                      onClick={selecting ? () => toggleSelected(m.id) : undefined}
                      data-testid={selecting ? `dm-selectable-${m.id}` : undefined}
                      className={`max-w-[75%] rounded-2xl px-4 py-2 transition-shadow ${
                        mine ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white' : 'bg-gray-100 text-gray-800'
                      } ${selecting ? 'cursor-pointer' : ''} ${
                        picked ? 'ring-2 ring-orange-500 ring-offset-2' : ''
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>
                      <p className={`text-[10px] mt-1 ${mine ? 'text-cyan-100' : 'text-gray-400'}`}>{timeLabel(m.created_at)}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            <div className="p-3 border-t flex items-center gap-2">
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Type a message…"
                data-testid="dm-input"
              />
              <Button onClick={send} disabled={sending || !draft.trim()} data-testid="dm-send-btn" className="bg-gradient-to-r from-cyan-500 to-purple-500">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}
      </div>

      {activeId && (
        <ReportConversationDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          conversationId={activeId}
          otherName={other?.full_name}
          selectedIds={selectedIds}
          onReported={afterReport}
        />
      )}
    </div>
  );
};

export default DirectMessages;
